import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ConfirmAllLinesBody,
  ConfirmAllLinesResult,
  ConfirmShipDateBody,
  ConfirmSplitBody,
  ConfirmSplitResult,
  CreateSplitsBody,
  CreateSplitsResult,
  LogEstimateBody,
  OdooLineQty,
  ShipConfirmationSplit,
  ShipConfirmationStage,
  ShipConfirmationTracker,
  ShipEstimateEntry,
} from './types'

const SHIP_CONFIRMATION_KEY = ['po-ship-confirmation'] as const

/**
 * Tracker rows — the board/table's data source. Defaults to the active
 * (non-terminal) stages server-side; pass `stage` to filter to one, or
 * `includeTerminal` to see received/cancelled too. Refetches every 30s so
 * reconciler-driven creates/resolves show up without a manual refresh.
 */
export function useShipConfirmationList(stage?: ShipConfirmationStage, includeTerminal?: boolean) {
  const params = new URLSearchParams()
  if (stage) params.set('stage', stage)
  if (includeTerminal) params.set('include_terminal', 'true')
  const qs = params.toString() ? `?${params.toString()}` : ''
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'list', stage ?? 'board', includeTerminal ?? false],
    queryFn: () => jsonFetch<ShipConfirmationTracker[]>(`/api/po-shipment-confirmation${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/**
 * Every tracker row for one PO — the detail drawer's data source.
 * `include_terminal=true` so a resolved (received/cancelled) PO still
 * shows its full line history if the drawer is reopened later, even
 * though the board itself only ever surfaces active POs. Reuses the
 * existing list route's `odoo_po_id` filter — no dedicated detail
 * endpoint needed.
 */
export function usePoShipConfirmationLines(odooPoId: number | null) {
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'by-po', odooPoId],
    queryFn: () =>
      jsonFetch<ShipConfirmationTracker[]>(
        `/api/po-shipment-confirmation?odoo_po_id=${odooPoId}&include_terminal=true`,
      ),
    enabled: odooPoId != null,
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/** Logged estimate history for one line (and its splits), newest first. */
export function useShipEstimateHistory(trackerId: number | null) {
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'estimates', trackerId],
    queryFn: () =>
      jsonFetch<ShipEstimateEntry[]>(`/api/po-shipment-confirmation/${trackerId}/estimates`),
    enabled: trackerId != null,
    staleTime: 15_000,
  })
}

export function useShipConfirmationStats() {
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'stats'],
    queryFn: () =>
      jsonFetch<Record<ShipConfirmationStage, number>>('/api/po-shipment-confirmation/stats'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/** Manual stage override (drag on the board, or a table row action). */
export function useTransitionShipConfirmation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackerId: number; targetStage: ShipConfirmationStage; note?: string }) =>
      jsonFetch<ShipConfirmationTracker>(
        `/api/po-shipment-confirmation/${vars.trackerId}/transition`,
        { method: 'POST', body: { target_stage: vars.targetStage, note: vars.note } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/**
 * Capture a vendor's confirmation — advances to `confirmed`, writes the
 * mirror fields onto the Odoo PO, and posts a chatter note. This is the
 * one action that requires evidence (date + source), so the UI always
 * routes it through `ConfirmShipDateModal` rather than a bare drag or a
 * one-click table action.
 */
export function useConfirmShipDate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackerId: number; body: ConfirmShipDateBody }) =>
      jsonFetch<ShipConfirmationTracker>(
        `/api/po-shipment-confirmation/${vars.trackerId}/confirm`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/**
 * "All lines ship together" — confirms every open line of a PO with one
 * date in a single request. Per Chris's decision, this ALWAYS overwrites
 * every open line's date, including lines with their own already-set
 * date — surfaced explicitly in `ConfirmShipDateModal`'s checkbox copy.
 */
export function useConfirmAllLines() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { odooPoId: number; body: ConfirmAllLinesBody }) =>
      jsonFetch<ConfirmAllLinesResult>(
        `/api/po-shipment-confirmation/by-po/${vars.odooPoId}/confirm-all`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/** Log a revised (still-unconfirmed) vendor estimate — no stage change. */
export function useLogShipDateEstimate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackerId: number; body: LogEstimateBody }) =>
      jsonFetch<ShipConfirmationTracker>(
        `/api/po-shipment-confirmation/${vars.trackerId}/log-estimate`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

// ─── Split shipments (2026-09-09) ─────────────────────────────────────

/**
 * Live ordered/received qty for one tracker's Odoo PO line — fetched
 * on-demand only while the split modal is open (not part of the
 * polling list/detail queries): a one-shot validation read, not a
 * live-polling row.
 */
export function useOdooLineQty(trackerId: number | null) {
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'odoo-line-qty', trackerId],
    queryFn: () => jsonFetch<OdooLineQty>(`/api/po-shipment-confirmation/${trackerId}/odoo-line-qty`),
    enabled: trackerId != null,
    staleTime: 0,
  })
}

/** Splits for one tracker — fetched lazily, only while its row is
 *  expanded (see `SplitRowsPanel`). */
export function useSplitsForTracker(trackerId: number | null) {
  return useQuery({
    queryKey: [...SHIP_CONFIRMATION_KEY, 'splits', trackerId],
    queryFn: () => jsonFetch<ShipConfirmationSplit[]>(`/api/po-shipment-confirmation/${trackerId}/splits`),
    enabled: trackerId != null,
    staleTime: 15_000,
  })
}

/** Break a tracker's line into N split shipments. */
export function useCreateSplits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackerId: number; body: CreateSplitsBody }) =>
      jsonFetch<CreateSplitsResult>(
        `/api/po-shipment-confirmation/${vars.trackerId}/splits`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/** "Unsplit" — deletes every split row for a tracker and restores it to
 *  a single line. `force: true` required once any split is
 *  confirmed/resolved (discards that evidence). */
export function useUnsplit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackerId: number; force: boolean }) =>
      jsonFetch<ShipConfirmationTracker>(
        `/api/po-shipment-confirmation/${vars.trackerId}/splits`,
        { method: 'DELETE', body: { force: vars.force } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/** Confirm one split — rolls the parent tracker's stage up (and mirrors
 *  to Odoo) once every sibling split is confirmed. */
export function useConfirmSplit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { splitId: number; body: ConfirmSplitBody }) =>
      jsonFetch<ConfirmSplitResult>(
        `/api/po-shipment-confirmation/splits/${vars.splitId}/confirm`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}

/** Log a revised (still-unconfirmed) estimate on one split. */
export function useLogSplitEstimate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { splitId: number; body: LogEstimateBody }) =>
      jsonFetch<ShipConfirmationSplit>(
        `/api/po-shipment-confirmation/splits/${vars.splitId}/log-estimate`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIP_CONFIRMATION_KEY })
    },
  })
}
